import { useEffect, useRef, useCallback } from 'react';
import logger from '../utils/logger';
import { getApiBase } from '../config/api';

/**
 * Hook that polls for new media in a flow at regular intervals
 * Used to detect completed jobs that were resumed after server restart
 * @param flowId - The flow ID to poll for
 * @param userId - The user ID for authentication
 * @param onMediaUpdated - Callback when new media is detected
 * @param pollIntervalMs - Polling interval in milliseconds (default: 60 seconds for large flows)
 */
export function useMediaPolling(
  flowId: string | null | undefined,
  userId: string | null | undefined,
  onMediaUpdated: (count: number) => void,
  pollIntervalMs: number = 60000
) {
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousMediaCountRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);

  const pollForMedia = useCallback(async () => {
    if (!flowId || !userId || !isMountedRef.current) {
      return;
    }

    try {
      const apiBase = getApiBase();
      const response = await fetch(
        `${apiBase}/api/media/list/flow/${encodeURIComponent(flowId)}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-user-id': userId,
          },
        }
      );

      if (!response.ok) {
        logger.debug('[useMediaPolling] Failed to fetch media list:', response.status);
        return;
      }

      const data = await response.json();
      const currentMediaCount = data.files?.length || 0;

      // Only log if count changed
      if (currentMediaCount !== previousMediaCountRef.current) {
        logger.debug(
          `[useMediaPolling] Media count updated: ${previousMediaCountRef.current} → ${currentMediaCount}`,
          { flowId }
        );
        previousMediaCountRef.current = currentMediaCount;
        onMediaUpdated(currentMediaCount);
      }
    } catch (error) {
      logger.debug('[useMediaPolling] Error polling for media:', error);
      // Don't throw - just silently fail and retry next interval
    }
  }, [flowId, userId, onMediaUpdated]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!flowId || !userId) {
      // Clear any existing interval
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    // Initial poll right away
    pollForMedia();

    // Set up recurring poll
    pollIntervalRef.current = setInterval(() => {
      pollForMedia();
    }, pollIntervalMs);

    logger.debug('[useMediaPolling] Started polling for media', { flowId, interval: pollIntervalMs });

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      logger.debug('[useMediaPolling] Stopped polling for media', { flowId });
    };
  }, [flowId, pollIntervalMs, pollForMedia]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);
}
